import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import FeatureBand from '../components/FeatureBand';
import SolutionsWorkflowStepper from '../components/SolutionsWorkflowStepper';
import SolutionsFunnelGraphic from '../components/SolutionsFunnelGraphic';
import './SolutionsPage.css';

const CANONICAL_SOLUTIONS_URL = 'https://circumvent.io/solutions';
const SOLUTIONS_PAGE_TITLE = 'Finding validation for Cloud and AppSec | Circumvent';
const SOLUTIONS_PAGE_DESCRIPTION =
  'Circumvent validates Cloud and AppSec findings through contextual prioritization, grouping by action, and an investigation verdict with evidence.';
const SOLUTIONS_HERO_VIDEO_SRC = '/videos/intelligence-core-animation.mp4';
const APP_CENTRIC_ILLUSTRATION = '/images/features/icon_App-centric-security.svg';
const VISUAL_INFRA_ILLUSTRATION = '/images/features/icon_Visual-infrastructure.svg';
const BUSINESS_CONTEXT_BULLET_DEFINE_APPLICATIONS = '/images/features/icon_apps.svg';
const BUSINESS_CONTEXT_BULLET_DISCOVER_SERVICES = '/images/features/icon_scan.svg';
const BUSINESS_CONTEXT_BULLET_VISUALIZE_ARCHITECTURE = '/images/features/icon_architecture.svg';
const BUSINESS_CONTEXT_BULLET_RESOURCE_RELATIONSHIPS = '/images/features/icon_resources.svg';

const BUSINESS_CONTEXT_ROWS = [
  {
    id: 'assets',
    headingId: 'solutions-business-context-assets-heading',
    title: 'Protect Your Most Important Assets',
    lead:
      "A good security program starts with an understanding of your cloud environment and which assets are considered your organization's crown jewels.",
    illustration: APP_CENTRIC_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 87,
    bullets: [
      {
        iconSrc: BUSINESS_CONTEXT_BULLET_DEFINE_APPLICATIONS,
        text: 'Define which Applications are most critical to your business.',
      },
      {
        iconSrc: BUSINESS_CONTEXT_BULLET_DISCOVER_SERVICES,
        text:
          'Scan your cloud and code to discover technical Services, and map them to Applications to define their boundaries.',
      },
    ],
  },
  {
    id: 'architecture',
    headingId: 'solutions-business-context-architecture-heading',
    title: 'Architecture and Risk in Context',
    lead:
      'Understand how your Applications have been built, what types of architecture your teams are using, and how your risks map to your cloud footprint.',
    illustration: VISUAL_INFRA_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 106,
    bullets: [
      {
        iconSrc: BUSINESS_CONTEXT_BULLET_VISUALIZE_ARCHITECTURE,
        text: 'Visualize your Application and Service architecture.',
      },
      {
        iconSrc: BUSINESS_CONTEXT_BULLET_RESOURCE_RELATIONSHIPS,
        text: 'Trace relationships between resources across your Application and Service graph.',
      },
    ],
  },
];

const UNIFIED_RISK_ILLUSTRATION = '/images/features/icon_Unified-security-risk.svg';
const INTELLIGENT_INSIGHTS_ILLUSTRATION = '/images/features/icon_Intelligent-insights.svg';
const CONTEXTUAL_PRIORITIZATION_ILLUSTRATION = '/images/features/icon_Contextual-prioritization.svg';
const ASSISTED_REMEDIATION_ILLUSTRATION = '/images/features/icon_Assisted-remediation.svg';
const TRIAGE_BULLET_AGGREGATE = '/images/features/icon_aggregate.svg';
const TRIAGE_BULLET_MAP = '/images/features/icon_map.svg';
const TRIAGE_BULLET_CORRELATE = '/images/features/icon_correlate.svg';
const TRIAGE_BULLET_INVESTIGATE = '/images/features/icon_investigate.svg';
const TRIAGE_BULLET_CONFIDENTIALITY = '/images/features/icon_confidentiality.svg';
const TRIAGE_BULLET_CODE_TO_CLOUD = '/images/features/icon_codetocloud.svg';
const REMEDIATION_BULLET_ESCALATE = '/images/features/icon_escalate.svg';
const REMEDIATION_BULLET_OWNER = '/images/features/icon_owner.svg';

const AUTONOMOUS_TRIAGE_ROWS = [
  {
    id: 'unified-risk',
    headingId: 'solutions-card-unified-risk-heading',
    title: 'Unified Security Risk',
    lead:
      'Stop juggling formats from every CSPM and CNAPP scanner. Normalize finding data schemas to the Open Cybersecurity Schema Framework (OCSF) for stronger correlation and intelligence.',
    illustration: UNIFIED_RISK_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 81,
    bullets: [
      {
        iconSrc: TRIAGE_BULLET_AGGREGATE,
        text: 'Aggregate findings across tools and triage from a single view.',
      },
      {
        iconSrc: TRIAGE_BULLET_MAP,
        text:
          'Consider security risk mapped to the correct Application context, with environment awareness.',
      },
    ],
  },
  {
    id: 'insights',
    headingId: 'solutions-card-insights-heading',
    title: 'Intelligent Insights powered by Agents',
    lead:
      'AI-powered intelligence correlates findings and investigates exposures at the scale and speed required for AI-powered software teams.',
    illustration: INTELLIGENT_INSIGHTS_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 93,
    bullets: [
      {
        iconSrc: TRIAGE_BULLET_CORRELATE,
        text: 'AI correlates security findings across code, cloud, and providers.',
      },
      {
        iconSrc: TRIAGE_BULLET_INVESTIGATE,
        text:
          'Agents investigate to autonomously triage and enrich findings with exposure and attack path analysis to reduce false positives.',
      },
    ],
  },
  {
    id: 'prioritization',
    headingId: 'solutions-card-prioritization-heading',
    title: 'Contextual Prioritization',
    lead:
      'Prioritize security insights by business context, application context, and environment awareness to reduce noise and allow teams to focus on the highest-impact findings.',
    illustration: CONTEXTUAL_PRIORITIZATION_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 111,
    bullets: [
      {
        iconSrc: TRIAGE_BULLET_CONFIDENTIALITY,
        text:
          'Consider Application criticality, data confidentiality, compliance requirements, and environment.',
      },
      {
        iconSrc: TRIAGE_BULLET_CODE_TO_CLOUD,
        text: 'Consider code-to-cloud relationships and deployment status.',
      },
    ],
  },
];

const ASSISTED_REMEDIATION_ROWS = [
  {
    id: 'remediation',
    headingId: 'solutions-card-remediation-heading',
    title: 'Resolve identified security risks',
    lead:
      'Stay connected to how engineering already plans and ships: open work in the right backlog, monitor time-to-remediate and nudge the team if tickets stall.',
    illustration: ASSISTED_REMEDIATION_ILLUSTRATION,
    illustrationWidth: 155,
    illustrationHeight: 67,
    bullets: [
      {
        iconSrc: REMEDIATION_BULLET_ESCALATE,
        text:
          'Reduce escalation to engineering by focusing on triaged insights that can be remediated.',
      },
      {
        iconSrc: REMEDIATION_BULLET_OWNER,
        text:
          'Understand which teams are responsible for which Services so security risk reaches the right owners through the right channels.',
      },
    ],
  },
];

const SolutionsPage = () => {
  const videoRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotionPreference = () => {
      setReduceMotion(mq.matches);
      if (mq.matches) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    };

    syncMotionPreference();
    mq.addEventListener('change', syncMotionPreference);
    return () => mq.removeEventListener('change', syncMotionPreference);
  }, []);

  return (
    <>
      <Helmet>
        <title>{SOLUTIONS_PAGE_TITLE}</title>
        <meta name="description" content={SOLUTIONS_PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_SOLUTIONS_URL} />
      </Helmet>
      <SeoSocialTags
        title={SOLUTIONS_PAGE_TITLE}
        description={SOLUTIONS_PAGE_DESCRIPTION}
        url={CANONICAL_SOLUTIONS_URL}
      />

      <main className="page page--light solutions-page">
        <section
          className="page-hero page-hero--dark page-hero--split"
          aria-labelledby="solutions-hero-heading"
        >
          <div className="page-container">
            <div className="page-hero--split__grid">
              <div className="page-hero--split__copy">
                <p className="page-hero__eyebrow">Solutions</p>
                <h1 id="solutions-hero-heading" className="page-hero__title">
                  Security aligned to your applications
                </h1>
                <p className="page-hero__lead">
                  For organizations with multiple applications, shared platform services, and distributed
                  ownership across security and engineering. Prioritize by business impact, from automated
                  scope to targeted remediation.
                </p>
              </div>
              <div
                className={`page-hero--split__media${reduceMotion ? ' page-hero--split__media--static' : ''}`}
                aria-hidden="true"
              >
                <video
                  ref={videoRef}
                  className="page-hero--split__video"
                  src={SOLUTIONS_HERO_VIDEO_SRC}
                  muted
                  playsInline
                  loop
                  autoPlay
                  preload="auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--white solutions-areas-panel"
          aria-labelledby="solutions-overview-heading"
        >
          <div className="page-container">
            <div className="section-panel__header">
              <p className="section-panel__eyebrow">Solution areas</p>
              <h2 id="solutions-overview-heading" className="section-panel__title">
                Built for high-impact risk reduction
              </h2>
              <p className="section-panel__intro">
                Circumvent correlates and prioritizes findings. Our autonomous triage agent investigates root
                cause against your actual cloud, code, and business context to post a verdict - false positive,
                risk accepted, or a precise remediation routed to the code owner.
              </p>
            </div>
          </div>
        </section>

        <FeatureBand
          sectionClassName="page-section page-section--light"
          titleId="solutions-business-context-heading"
          eyebrow="Business Context"
          title="From resources to ownership"
          rows={BUSINESS_CONTEXT_ROWS}
          cta={
            <>
              <Link to="/solutions/context" className="btn btn-solid">
                Explore Business Context
              </Link>
              <Link to="/solutions/agents" className="btn btn-outline">
                Explore Agents
              </Link>
            </>
          }
        />

        <FeatureBand
          sectionClassName="page-section page-section--white"
          titleId="solutions-workflow-heading"
          eyebrow="Autonomous Triage"
          title="From finding to decision"
          workflow={
            <SolutionsWorkflowStepper
              theme="light"
              showEyebrow={false}
              showTitle={false}
              includeSrTitle={false}
            />
          }
          rows={AUTONOMOUS_TRIAGE_ROWS}
          cta={
            <>
              <Link to="/solutions/automated-triage" className="btn btn-solid">
                Explore Automated Triage
              </Link>
              <Link to="/solutions/agents" className="btn btn-outline">
                Explore Agents
              </Link>
            </>
          }
        />

        <FeatureBand
          sectionClassName="page-section page-section--light"
          titleId="solutions-funnel-heading"
          eyebrow="Assisted Remediation"
          title="From findings to remediated"
          workflow={
            <SolutionsFunnelGraphic theme="light" showEyebrow={false} showTitle={false} />
          }
          rows={ASSISTED_REMEDIATION_ROWS}
          cta={
            <>
              <Link to="/solutions/assisted-remediation" className="btn btn-solid">
                Explore Assisted Remediation
              </Link>
              <Link to="/solutions/agents" className="btn btn-outline">
                Explore Agents
              </Link>
            </>
          }
        />
      </main>
    </>
  );
};

export default SolutionsPage;
